import type { ChatMessage } from '@reados/contracts';
import { isAsyncAgentMessage } from '@reados/contracts';
import type { TranscriptPayload } from './projection';

export interface TranscriptForkAction {
  /** Original final-answer ChatMessage.id, never the merged article ID. */
  messageID: string;
  threadId: string;
  turnId: string;
}

function isFinalReplySource(message: ChatMessage): boolean {
  if (isAsyncAgentMessage(message)) return false;
  if (message.phase !== undefined && message.phase !== null && message.phase !== 'final_answer') return false;
  const native = message.items?.filter(item => item.id === message.id);
  if (!native?.length) return message.phase === 'final_answer';
  return native.length === 1 && native[0].type === 'agentMessage' && native[0].text === message.text
    && (native[0].phase === undefined || native[0].phase === null || native[0].phase === 'final_answer');
}

/** The iframe names a visible article only. Native ownership and the inclusive
 * fork boundary are resolved again from the parent's unchanged source catalog. */
export function resolveTranscriptFork(messageID: string, messages: ChatMessage[], payload: TranscriptPayload): TranscriptForkAction | null {
  const article = payload.messages.find(message => message.id === messageID && message.role === 'assistant');
  if (!article || article.status === 'streaming' || !Array.isArray(article.blocks)) return null;
  const catalog = new Map(payload.blockCatalog.map(block => [block.id, block]));
  const source = new Map(messages.map(message => [message.id, message]));
  let final: ChatMessage | undefined;
  for (const id of article.blocks) {
    const block = catalog.get(id);
    // Match the copied footer's final-body predicate. Historical canonical
    // agentMessage items may omit phase; explicit commentary and plan items
    // must never acquire a fork capability from a missing phase alone.
    if (block?.type !== 'main_text' || block.readexProcessingFoldGroupID || block.readexProcessingFoldGroupId
      || typeof block.text !== 'string' || !block.text.trim()) continue;
    const message = source.get(String(block.sourceBlockId));
    if (!message || message.text !== block.text || typeof message.threadId !== 'string' || !message.threadId || typeof message.turnId !== 'string' || !message.turnId
      || message.threadId !== article.threadId || message.turnId !== article.readexCodexTurnID) return null;
    // A merged assistant article can also contain a source-backed native plan.
    // Its presence does not erase the later final reply's fork capability.
    if (message.role !== 'assistant' || !isFinalReplySource(message)) continue;
    if (message.status === 'streaming') return null;
    final = message;
  }
  return final ? { messageID: final.id, threadId: final.threadId as string, turnId: final.turnId as string } : null;
}

/** Port of ReadexTranscriptPayloadSupport.timeText for the Chinese Web UI.
 * This is the source turn's wall-clock time, not an elapsed duration. */
const timeFormatters = new Map<string, { date: Intl.DateTimeFormat; time: Intl.DateTimeFormat; weekday: Intl.DateTimeFormat }>();
function transcriptTimeFormatters(timeZone?: string) {
  const key = timeZone ?? '';
  const existing = timeFormatters.get(key);
  if (existing) return existing;
  const options = timeZone ? { timeZone } : {};
  const formatters = {
    date: new Intl.DateTimeFormat('en-US', { ...options, calendar: 'gregory', year: 'numeric', month: 'numeric', day: 'numeric' }),
    time: new Intl.DateTimeFormat('zh-CN', { ...options, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }),
    weekday: new Intl.DateTimeFormat('zh-CN', { ...options, weekday: 'long' }),
  };
  timeFormatters.set(key, formatters);
  if (timeFormatters.size > 8) timeFormatters.delete(timeFormatters.keys().next().value!);
  return formatters;
}

export function transcriptTimeText(milliseconds: number | undefined, now = Date.now(), timeZone?: string): string {
  if (typeof milliseconds !== 'number' || !Number.isFinite(milliseconds) || milliseconds <= 0
    || !Number.isFinite(new Date(milliseconds).getTime()) || !Number.isFinite(new Date(now).getTime())) return '';
  const formatters = transcriptTimeFormatters(timeZone);
  const dateParts = (value: number) => Object.fromEntries(formatters.date.formatToParts(value).filter(part => part.type !== 'literal').map(part => [part.type, Number(part.value)]));
  const date = dateParts(milliseconds), today = dateParts(now);
  const days = (Date.UTC(date.year, date.month - 1, date.day) - Date.UTC(today.year, today.month - 1, today.day)) / 86_400_000;
  const time = formatters.time.format(milliseconds);
  if (days === 0) return time;
  if (days >= -6 && days <= -1) return formatters.weekday.format(milliseconds) + time;
  return `${date.month}月${date.day}日 ${time}`;
}
